import { useMappingWorkspaceDiagnostics } from './useMappingWorkspaceDiagnostics';
import type { MappingWorkspaceDiagnosticsInput } from './useMappingWorkspaceDiagnostics';
import type { useMappingProjectReadiness } from './useMappingProjectReadiness';
import type { useProjectProfileGate } from './useProjectProfileGate';
import type { RepairConsentGateResult } from './useRepairConsentGate';

type ProjectProfileGate = ReturnType<typeof useProjectProfileGate>;
type MappingReadiness = ReturnType<typeof useMappingProjectReadiness>;

export interface UseMappingWorkspaceDiagnosticsWiringArgs {
  processConfig: MappingWorkspaceDiagnosticsInput['processConfig'];
  activeDocumentInfo: MappingWorkspaceDiagnosticsInput['activeDocumentInfo'];
  gate: ProjectProfileGate;
  mappingReadiness: MappingReadiness;
  repairConsentGate: Pick<
    RepairConsentGateResult,
    | 'mappingReadinessEnabled'
    | 'profileAutoRepairAllowed'
    | 'profileAutoRepairBlockedMessage'
    | 'confirmedRepairProfileKey'
    | 'resolvedProfileMissingPreview'
  >;
  flags: {
    dockablePaneVisible: boolean;
    isLiveWizardMappingHandoff: boolean;
    hasResolvedSelectedProfile: boolean;
    resolvedProfileName: string;
    profilesLoading: boolean;
    lastAppliedProfileKey: string;
    selectedProfileKey: string;
    projectIdentityPending: boolean;
    projectPreferredProfileKey: string | null;
    activeTab: number;
  };
  applyAttention: MappingWorkspaceDiagnosticsInput['applyAttention'];
}

/**
 * Adapter between the content model's raw state and the diagnostics sink:
 * assembles the grouped `MappingWorkspaceDiagnosticsInput` (gate field
 * selection, trust-source flattening, repair-gate flags) so the composition
 * root does not carry the mapping inline. Emits nothing itself.
 */
export function useMappingWorkspaceDiagnosticsWiring({
  processConfig,
  activeDocumentInfo,
  gate,
  mappingReadiness,
  repairConsentGate,
  flags,
  applyAttention,
}: UseMappingWorkspaceDiagnosticsWiringArgs): void {
  useMappingWorkspaceDiagnostics({
    processConfig,
    activeDocumentInfo,
    gate: {
      activeProjectSessionKey: gate.activeProjectSessionKey,
      resolvedProfileKey: gate.resolvedProfileKey,
      resolvedSettingsType: gate.resolvedSettingsType,
      resumeProfileKey: gate.resumeProfileKey,
      resumeScopeKey: gate.resumeScopeKey,
      profileConfirmedForCurrentSession: gate.profileConfirmedForCurrentSession,
      projectSessionConfirmedProfileKey: gate.projectSessionConfirmedProfileKey,
      projectSessionProfileConfirmationSource: gate.projectSessionProfileConfirmationSource,
      restorePersistedWorkspace: gate.restorePersistedWorkspace,
      projectProfileTrustSource: gate.projectProfileTrust?.source ?? null,
      projectProfileGateDecision: gate.projectProfileGateDecision,
      projectProfileGateEvaluationNeeded: gate.projectProfileGateEvaluationNeeded,
      projectProfileGateOpen: gate.projectProfileGateOpen,
      projectProfileAutoPassProfileKey: gate.projectProfileAutoPassProfileKey,
      projectProfileAutoPassReason: gate.projectProfileAutoPassReason,
      projectProfileAutoPassBlocked: gate.projectProfileAutoPassBlocked,
      projectProfileAutoPassLoadedKey: gate.projectProfileAutoPassLoadedKey,
      manualProjectProfileSelectionActive: gate.manualProjectProfileSelectionActive,
      quickFillProfileRestorePending: gate.quickFillProfileRestorePending,
      profileProjectMatches: gate.profileProjectMatches,
    },
    readiness: mappingReadiness,
    flags: {
      mappingReadinessEnabled: repairConsentGate.mappingReadinessEnabled,
      dockablePaneVisible: flags.dockablePaneVisible,
      profileAutoRepairAllowed: repairConsentGate.profileAutoRepairAllowed,
      profileAutoRepairBlockedMessage: repairConsentGate.profileAutoRepairBlockedMessage,
      isLiveWizardMappingHandoff: flags.isLiveWizardMappingHandoff,
      hasResolvedSelectedProfile: flags.hasResolvedSelectedProfile,
      confirmedRepairProfileKey: repairConsentGate.confirmedRepairProfileKey,
      resolvedProfileName: flags.resolvedProfileName,
      resolvedProfileMissingPreview: repairConsentGate.resolvedProfileMissingPreview,
      profilesLoading: flags.profilesLoading,
      lastAppliedProfileKey: flags.lastAppliedProfileKey,
      selectedProfileKey: flags.selectedProfileKey,
      projectIdentityPending: flags.projectIdentityPending,
      projectPreferredProfileKey: flags.projectPreferredProfileKey,
      activeTab: flags.activeTab,
    },
    applyAttention,
  });
}
